import React, {
  FC,
  ReactNode,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FastField, Field, FieldProps } from 'formik';
import { flushSync } from 'react-dom';

import InputAdornment from '@mui/material/InputAdornment';
import TextField, { TextFieldProps } from '@mui/material/TextField';

import { FieldWrapper } from '~components/form/field-wrapper/field-wrapper';

export type BasicTextFieldProps = {
  name: string;

  startIcon?: ReactNode;
  endIcon?: ReactNode;
  iconPosition?: 'start' | 'end';
  onAdornmentClick?: (event: React.SyntheticEvent) => void;
  labelIcon?: ReactNode;
  info?: ReactNode;
  readOnly?: boolean;

  dynamic?: boolean;

  deferFormik?: boolean;
  deferMs?: number;
  commitOnBlur?: boolean;
} & TextFieldProps;

type InnerProps = BasicTextFieldProps & FieldProps;

const BaseTextFieldInner: FC<InnerProps> = React.memo(
  ({
    name,
    startIcon,
    endIcon,
    label,
    helperText,
    info,
    form,
    field,
    meta,

    deferFormik = true,
    deferMs = 180,
    commitOnBlur = true,

    ...props
  }) => {
    const showError = Boolean(meta.touched && meta.error);

    const hasCustomInputComponent = Boolean(
      (props as any).InputProps?.inputComponent,
    );

    const isReadOnly =
      Boolean((props as any).readOnly) ||
      Boolean((props as any).InputProps?.readOnly);

    const [localValue, setLocalValue] = useState<string>(() =>
      field.value == null ? '' : String(field.value),
    );

    const localValueRef = useRef<string>(
      field.value == null ? '' : String(field.value),
    );

    const lastCommittedRef = useRef<string>(
      field.value == null ? '' : String(field.value),
    );

    const pendingCommitRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null);

    useEffect(() => {
      if (!deferFormik) return;

      const next = field.value == null ? '' : String(field.value);

      if (pendingCommitRef.current !== null) {
        if (next === pendingCommitRef.current) {
          pendingCommitRef.current = null;
          lastCommittedRef.current = next;

          if (localValueRef.current === next) {
            setLocalValue(next);
          }
        }

        return;
      }

      if (next !== lastCommittedRef.current) {
        lastCommittedRef.current = next;
        localValueRef.current = next;
        setLocalValue(next);
      }
    }, [deferFormik, field.value]);

    useEffect(() => {
      return () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
      };
    }, []);

    const shouldValidateCommittedValue = () =>
      Boolean(meta.touched || form.submitCount > 0);

    const commit = (
      val: string,
      shouldValidate = shouldValidateCommittedValue(),
    ) => {
      if (isReadOnly || Boolean(props.disabled)) return;
      if (val === lastCommittedRef.current) return;

      pendingCommitRef.current = val;
      flushSync(() => {
        form.setFieldValue(name, val, shouldValidate);
      });
    };

    const scheduleCommit = (val: string) => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => commit(val), deferMs);
    };

    const syncInputValue = (val: string) => {
      if (isReadOnly || Boolean(props.disabled)) return;

      if (!deferFormik) {
        form.setFieldValue(name, val, shouldValidateCommittedValue());
        return;
      }

      if (val !== localValueRef.current) {
        localValueRef.current = val;
        setLocalValue(val);
        scheduleCommit(val);
      }
    };

    const InputProps = useMemo(() => {
      const base = (props as any).InputProps ?? {};
      const baseInputProps = (props as any).inputProps ?? {};
      const baseInputPropsFromInputProps = base.inputProps ?? {};

      const computed = {
        ...(startIcon
          ? {
              startAdornment: (
                <InputAdornment
                  position="start"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {startIcon}
                </InputAdornment>
              ),
            }
          : {}),
        ...(endIcon
          ? {
              endAdornment: (
                <InputAdornment
                  position="end"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {endIcon}
                </InputAdornment>
              ),
            }
          : {}),
      };

      const resolvedReadOnly =
        base.readOnly ??
        baseInputPropsFromInputProps.readOnly ??
        baseInputProps.readOnly ??
        (props as any).readOnly ??
        undefined;

      return {
        ...base,
        ...computed,
        readOnly: resolvedReadOnly,
        inputProps: {
          ...baseInputProps,
          ...baseInputPropsFromInputProps,
          readOnly: resolvedReadOnly,
        },
      };
    }, [
      startIcon,
      endIcon,
      (props as any).InputProps,
      (props as any).inputProps,
      (props as any).readOnly,
    ]);

    return (
      <FieldWrapper
        name={field.name}
        label={label}
        info={info}
        hint={helperText}
        error={showError ? (meta.error as any) : undefined}
        htmlFor={field.name}
        required={props.required}
        disabled={props.disabled}
      >
        <TextField
          {...(props as any)}
          {...field}
          id={name}
          name={name}
          InputProps={InputProps}
          error={showError}
          helperText={undefined}
          fullWidth
          value={deferFormik ? localValue : (field.value ?? '')}
          onChange={(e) => {
            if (isReadOnly || Boolean(props.disabled)) return;

            if (!deferFormik) {
              field.onChange(e);
              (props as any).onChange?.(e);
              return;
            }

            const val = e.target.value;

            localValueRef.current = val;
            setLocalValue(val);
            scheduleCommit(val);

            (props as any).onChange?.(e);
          }}
          onInput={(e: React.FormEvent<HTMLInputElement>) => {
            if (!hasCustomInputComponent) {
              syncInputValue(e.currentTarget.value ?? '');
            }

            (props as any).onInput?.(e);
          }}
          onBlur={(e) => {
            if (
              deferFormik &&
              commitOnBlur &&
              !(isReadOnly || Boolean(props.disabled))
            ) {
              if (timerRef.current) window.clearTimeout(timerRef.current);
              commit(localValueRef.current, false);
            }

            field.onBlur(e);
            (props as any).onBlur?.(e);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            (props as any).onKeyDown?.(e);

            if (
              e.defaultPrevented ||
              !deferFormik ||
              e.key !== 'Enter' ||
              e.shiftKey ||
              e.metaKey ||
              e.ctrlKey ||
              e.altKey ||
              props.multiline ||
              isReadOnly ||
              Boolean(props.disabled)
            ) {
              return;
            }

            if (timerRef.current) window.clearTimeout(timerRef.current);
            commit(localValueRef.current, false);
          }}
        />
      </FieldWrapper>
    );
  },
);

export const BaseTextField: FC<BasicTextFieldProps> = React.memo((props) => {
  const { name, dynamic } = props;
  const WrapperField = dynamic ? Field : FastField;

  return (
    <WrapperField name={name}>
      {(fp: FieldProps) => <BaseTextFieldInner {...props} {...fp} />}
    </WrapperField>
  );
});

// import React, {
//   FC,
//   ReactNode,
//   useEffect,
//   useMemo,
//   useRef,
//   useState,
// } from 'react';
//
// import { FastField, Field, FieldProps } from 'formik';
//
// import InputAdornment from '@mui/material/InputAdornment';
// import TextField, { TextFieldProps } from '@mui/material/TextField';
//
// import { FieldWrapper } from '~components/form/field-wrapper/field-wrapper';
//
// export type BasicTextFieldProps = {
//   name: string;
//
//   startIcon?: ReactNode;
//   endIcon?: ReactNode;
//   iconPosition?: 'start' | 'end';
//   onAdornmentClick?: (event: React.SyntheticEvent) => void;
//   labelIcon?: ReactNode;
//   info?: ReactNode;
//   readOnly?: boolean;
//
//   dynamic?: boolean;
//
//   /** ✅ включает локальный ввод без лагов */
//   deferFormik?: boolean;
//
//   /** задержка синка в Formik (мс) */
//   deferMs?: number;
//
//   /** коммитить в Formik на blur (обычно true) */
//   commitOnBlur?: boolean;
// } & TextFieldProps;
//
// type InnerProps = BasicTextFieldProps & FieldProps;
//
// const BaseTextFieldInner: FC<InnerProps> = React.memo(
//   ({
//     name,
//     startIcon,
//     endIcon,
//     label,
//     helperText,
//     info,
//     form,
//     field,
//     meta,
//
//     deferFormik = true,
//     deferMs = 180,
//     commitOnBlur = true,
//
//     ...props
//   }) => {
//     const showError = Boolean(meta.touched && meta.error);
//
//     // ✅ Учитываем readOnly как "нельзя редактировать"
//     const isReadOnly =
//       Boolean((props as any).readOnly) ||
//       Boolean((props as any).InputProps?.readOnly);
//
//     // ---- Local state (только если deferFormik=true) ----
//     const [localValue, setLocalValue] = useState<string>(() =>
//       field.value == null ? '' : String(field.value)
//     );
//
//     // чтобы не было лишних коммитов/петель
//     const lastCommittedRef = useRef<string>(
//       field.value == null ? '' : String(field.value)
//     );
//     const timerRef = useRef<number | null>(null);
//
//     // если значение поля меняется извне (setValues/resetForm) - синкаем local
//     useEffect(() => {
//       if (!deferFormik) return;
//
//       const next = field.value == null ? '' : String(field.value);
//
//       // если Formik обновил значение (не мы) - подхватим
//       if (next !== lastCommittedRef.current) {
//         lastCommittedRef.current = next;
//         setLocalValue(next);
//       }
//     }, [deferFormik, field.value]);
//
//     useEffect(() => {
//       return () => {
//         if (timerRef.current) window.clearTimeout(timerRef.current);
//       };
//     }, []);
//
//     const commit = (val: string) => {
//       // ✅ если readOnly/disabled - не коммитим
//       if (isReadOnly || Boolean(props.disabled)) return;
//
//       // не спамим setFieldValue одинаковыми значениями
//       if (val === lastCommittedRef.current) return;
//       lastCommittedRef.current = val;
//
//       // 3-й аргумент validate=false - у тебя validateOnChange=false, но пусть будет явно
//       form.setFieldValue(name, val, false);
//     };
//
//     const scheduleCommit = (val: string) => {
//       if (timerRef.current) window.clearTimeout(timerRef.current);
//       timerRef.current = window.setTimeout(() => commit(val), deferMs);
//     };
//
//     const InputProps = useMemo(() => {
//       const base = (props as any).InputProps ?? {};
//       const baseInputProps = (props as any).inputProps ?? {};
//       const baseInputPropsFromInputProps = base.inputProps ?? {};
//
//       const computed = {
//         ...(startIcon
//           ? {
//               startAdornment: (
//                 <InputAdornment
//                   position="start"
//                   onMouseDown={(e) => e.preventDefault()}
//                 >
//                   {startIcon}
//                 </InputAdornment>
//               ),
//             }
//           : {}),
//         ...(endIcon
//           ? {
//               endAdornment: (
//                 <InputAdornment
//                   position="end"
//                   onMouseDown={(e) => e.preventDefault()}
//                 >
//                   {endIcon}
//                 </InputAdornment>
//               ),
//             }
//           : {}),
//       };
//
//       // ✅ MUI readOnly работает через InputProps.readOnly и/или inputProps.readOnly
//       const resolvedReadOnly =
//         base.readOnly ??
//         baseInputPropsFromInputProps.readOnly ??
//         baseInputProps.readOnly ??
//         (props as any).readOnly ??
//         undefined;
//
//       return {
//         ...base,
//         ...computed,
//
//         // ✅ важная строка
//         readOnly: resolvedReadOnly,
//
//         // ✅ иногда устойчивее, когда readOnly есть и здесь
//         inputProps: {
//           ...baseInputProps,
//           ...baseInputPropsFromInputProps,
//           readOnly: resolvedReadOnly,
//         },
//       };
//     }, [
//       startIcon,
//       endIcon,
//       (props as any).InputProps,
//       (props as any).inputProps,
//       (props as any).readOnly,
//     ]);
//
//     return (
//       <FieldWrapper
//         name={field.name}
//         label={label}
//         info={info}
//         hint={helperText}
//         error={showError ? (meta.error as any) : undefined}
//         htmlFor={field.name}
//         required={props.required}
//         disabled={props.disabled}
//       >
//         <TextField
//           {...(props as any)}
//           {...field} // оставим name/onBlur etc., но value/onChange переопределим ниже
//           id={name}
//           name={name}
//           InputProps={InputProps}
//           error={showError}
//           helperText={undefined}
//           fullWidth
//           value={deferFormik ? localValue : field.value ?? ''}
//           onChange={(e) => {
//             // ✅ readOnly / disabled - полностью блокируем изменения
//             if (isReadOnly || Boolean(props.disabled)) return;
//
//             if (!deferFormik) {
//               field.onChange(e);
//               (props as any).onChange?.(e);
//               return;
//             }
//
//             const val = e.target.value;
//             setLocalValue(val);
//             scheduleCommit(val);
//
//             // если у тебя есть props.onChange - не ломаем
//             (props as any).onChange?.(e);
//           }}
//           onBlur={(e) => {
//             // touched должен ставиться
//             field.onBlur(e);
//
//             if (
//               deferFormik &&
//               commitOnBlur &&
//               !(isReadOnly || Boolean(props.disabled))
//             ) {
//               // форсим коммит перед уходом фокуса
//               if (timerRef.current) window.clearTimeout(timerRef.current);
//               commit(localValue);
//             }
//
//             (props as any).onBlur?.(e);
//           }}
//         />
//       </FieldWrapper>
//     );
//   }
// );
//
// export const BaseTextField: FC<BasicTextFieldProps> = React.memo((props) => {
//   const { name, dynamic } = props;
//   const WrapperField = dynamic ? Field : FastField;
//
//   return (
//     <WrapperField name={name}>
//       {(fp: FieldProps) => <BaseTextFieldInner {...props} {...fp} />}
//     </WrapperField>
//   );
// });
